import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { CurrencyDisplay } from '@/components/ui/stat-card';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { formatBusinessDate, formatCurrency } from '@/lib/format';
import { REFUND_EPSILON, remainingRefundable } from '@/lib/depositStatus';
import { useDepositCacheInvalidator, useDepositRefunds } from '@/hooks/useContractDeposits';
import type { DepositCurrency, DepositRefundRow, DepositRow } from './types';

/**
 * Refund surface for one deposit: record refunds and audit them in one sheet.
 *
 * A refund is a DEBIT transaction assigned to the deposit — the mirror of the
 * CREDIT the driver paid in. Writes go through the `record_deposit_refund` RPC
 * (race-safe, friendly over-refund errors); deletes are a plain PostgREST DELETE
 * because removing a refund only lowers the sum, so there is no invariant to guard.
 */

interface RefundDepositSheetProps {
    /** The deposit being refunded; null closes the sheet. */
    deposit: DepositRow | null;
    onOpenChange: (open: boolean) => void;
}

/** Shape TransactionSelector hands back on pick (its own local interface). */
interface SelectedTransaction {
    id: number;
    amount: number;
    transaction_at: string;
    description: string | null;
    type: string;
}

function driverName(deposit: DepositRow): string {
    return [deposit.driver_first_name, deposit.driver_middle_name, deposit.driver_last_name]
        .filter(Boolean)
        .join(' ');
}

function driverInitials(deposit: DepositRow): string {
    const first = deposit.driver_first_name?.[0] ?? '';
    const last = deposit.driver_last_name?.[0] ?? '';
    return (first + last).toUpperCase() || '—';
}

export default function RefundDepositSheet({ deposit, onOpenChange }: RefundDepositSheetProps) {
    const invalidateDepositCaches = useDepositCacheInvalidator();

    const [transactionId, setTransactionId] = useState<number | null>(null);
    const [selectedTx, setSelectedTx] = useState<SelectedTransaction | null>(null);
    const [remarks, setRemarks] = useState('');
    const [pendingDelete, setPendingDelete] = useState<DepositRefundRow | null>(null);

    const depositId = deposit?.id ?? null;
    const { data: refunds, isLoading: isLoadingRefunds } = useDepositRefunds(depositId);

    // Reset the form whenever the sheet is pointed at a different deposit (or closed).
    useEffect(() => {
        setTransactionId(null);
        setSelectedTx(null);
        setRemarks('');
        setPendingDelete(null);
    }, [depositId]);

    /**
     * TransactionSelector's row shape carries no `currency`, so the mismatch check
     * reads it off the transaction directly. Own query key — ['transaction', id]
     * belongs to the selector and holds a narrower select.
     */
    const { data: selectedTxCurrency } = useQuery<DepositCurrency | null>({
        queryKey: ['transaction-currency', transactionId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('transactions')
                .select('currency')
                .eq('id', transactionId as number)
                .single();
            if (error) throw error;
            return (data?.currency ?? null) as DepositCurrency | null;
        },
        enabled: transactionId != null,
        staleTime: 60000,
    });

    /**
     * Refund total: summed from the (invalidated, refetched) refunds query so the
     * tiles stay truthful after each refund — the `deposit` row prop is a snapshot
     * the parent took when the sheet opened and goes stale on the second refund.
     */
    const refundedTotal = useMemo(() => {
        if (!deposit) return 0;
        if (!refunds) return deposit.refunded_total;
        return Math.round(refunds.reduce((sum, r) => sum + r.amount, 0) * 100) / 100;
    }, [deposit, refunds]);

    const depositAmount = deposit?.amount ?? 0;
    const depositCurrency: DepositCurrency = deposit?.currency ?? 'GHS';
    const remaining = remainingRefundable(depositAmount, refundedTotal);
    const fullyRefunded = remaining <= REFUND_EPSILON;
    const previewCurrency = selectedTxCurrency ?? depositCurrency;

    const errors = useMemo(() => {
        const list: string[] = [];
        if (!deposit) return list;
        if (!transactionId || !selectedTx) {
            list.push('Select the DEBIT transaction for this refund');
            return list;
        }
        if (selectedTx.type !== 'DEBIT') {
            list.push('Refunds must be recorded against a DEBIT transaction');
        }
        if (selectedTxCurrency && selectedTxCurrency !== deposit.currency) {
            list.push(`Transaction is in ${selectedTxCurrency}; this deposit is in ${deposit.currency}`);
        }
        if (selectedTx.amount <= 0) {
            list.push('Refund amount must be greater than zero');
        }
        if (selectedTx.amount > remaining + REFUND_EPSILON) {
            list.push(
                `Refund of ${formatCurrency(selectedTx.amount, previewCurrency)} exceeds the ${formatCurrency(remaining, deposit.currency)} still refundable`,
            );
        }
        return list;
    }, [deposit, transactionId, selectedTx, selectedTxCurrency, remaining, previewCurrency]);

    const refundMutation = useMutation({
        mutationFn: async () => {
            if (!deposit || !transactionId) throw new Error('Select the DEBIT transaction for this refund');
            const { data, error } = await supabase.rpc('record_deposit_refund', {
                p_contract_deposit_id: deposit.id,
                p_transaction_id: transactionId,
                p_remarks: remarks.trim() || null,
            });
            if (error) throw error;
            return data;
        },
        onSuccess: () => {
            toast.success('Refund recorded');
            invalidateDepositCaches({ depositId: deposit?.id, contractId: deposit?.contract_id });
            // Form resets, sheet stays open — partial refunds are usually recorded in a run.
            setTransactionId(null);
            setSelectedTx(null);
            setRemarks('');
        },
        onError: (e: unknown) => {
            const err = e as { code?: string; message?: string };
            const duplicate = err?.code === '23505' || /duplicate key/i.test(err?.message ?? '');
            // RPC messages ('Refund of X exceeds...', 'Access denied') are already friendly.
            toast.error(duplicate ? 'That transaction is already assigned to a refund.' : err?.message || 'Could not record the refund');
        },
    });

    const deleteRefundMutation = useMutation({
        mutationFn: async (refundId: number) => {
            const { error } = await supabase
                .from('contract_deposit_refunds')
                .delete()
                .eq('id', refundId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Refund removed');
            invalidateDepositCaches({ depositId: deposit?.id, contractId: deposit?.contract_id });
            setPendingDelete(null);
        },
        onError: (e: unknown) => {
            const err = e as { message?: string };
            toast.error(err?.message || 'Could not remove the refund');
        },
    });

    const handleClose = () => onOpenChange(false);

    return (
        <>
            <Sheet open={!!deposit} onOpenChange={(next) => { if (!next) handleClose(); }}>
                {deposit && (
                    <SheetContent
                        side="right"
                        className="!inset-y-4 !right-4 !h-auto !w-[46vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
                    >
                        <SheetHeader>
                            <SheetTitle>Refund Deposit</SheetTitle>
                        </SheetHeader>

                        <div className="flex-1 mt-6 space-y-6 overflow-y-auto pr-1">
                            {/* Identity */}
                            <div className="flex items-center justify-between gap-4">
                                {deposit.driver_first_name || deposit.driver_last_name ? (
                                    <div className="flex items-center gap-3">
                                        <Avatar className="h-10 w-10">
                                            <SignedAvatarImage src={deposit.driver_photo || undefined} alt={driverName(deposit)} />
                                            <AvatarFallback className="text-xs bg-muted">{driverInitials(deposit)}</AvatarFallback>
                                        </Avatar>
                                        <div>
                                            <p className="font-medium">{driverName(deposit)}</p>
                                            {deposit.driver_phone && (
                                                <p className="text-xs text-muted-foreground">{deposit.driver_phone}</p>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-muted-foreground whitespace-pre-line">
                                        {deposit.remarks || <span className="italic">— no depositor details —</span>}
                                    </p>
                                )}
                                {deposit.vehicle_registration && (
                                    <span className="text-sm font-medium shrink-0">{deposit.vehicle_registration}</span>
                                )}
                            </div>

                            {/* Money tiles */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="p-4 rounded-lg border bg-muted/40">
                                    <span className="text-xs text-muted-foreground block mb-1">Deposit</span>
                                    <span className="text-xl font-bold tabular-nums">
                                        <CurrencyDisplay amount={depositAmount} currency={depositCurrency} />
                                    </span>
                                </div>
                                <div className="p-4 rounded-lg border bg-muted/40">
                                    <span className="text-xs text-muted-foreground block mb-1">Refunded</span>
                                    <span className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                                        <CurrencyDisplay amount={refundedTotal} currency={depositCurrency} />
                                    </span>
                                </div>
                                <div className="p-4 rounded-lg border bg-muted/40">
                                    <span className="text-xs text-muted-foreground block mb-1">Remaining</span>
                                    <span
                                        className={`text-xl font-bold tabular-nums ${remaining > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
                                    >
                                        <CurrencyDisplay amount={remaining} currency={depositCurrency} />
                                    </span>
                                </div>
                            </div>

                            {/* Record a refund */}
                            {fullyRefunded ? (
                                <div className="rounded-lg border border-dashed p-4 text-center">
                                    <p className="text-sm font-medium">Fully refunded</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Nothing remains to refund. Remove a refund below to record a different one.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    <div className="space-y-2">
                                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                            Refund Transaction *
                                        </Label>
                                        <TransactionSelector
                                            value={transactionId}
                                            onChange={(id, transaction) => {
                                                setTransactionId(id);
                                                setSelectedTx(transaction ?? null);
                                            }}
                                            placeholder="Select the outgoing payment..."
                                            defaultType="DEBIT"
                                        />
                                        {transactionId && errors.length > 0 ? (
                                            <p className="text-xs text-destructive">{errors[0]}</p>
                                        ) : (
                                            <p className="text-xs text-muted-foreground">
                                                The outgoing DEBIT that paid the driver back. Not in the list? Use “Create New
                                                Transaction” in the picker — it pre-fills a DEBIT.
                                            </p>
                                        )}
                                    </div>

                                    {selectedTx && (
                                        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40">
                                            <span className="text-xs text-muted-foreground block mb-1">Refund Amount</span>
                                            <span className="text-2xl font-bold tabular-nums text-red-600 dark:text-red-400">
                                                −{formatCurrency(selectedTx.amount, previewCurrency)}
                                            </span>
                                            <p className="text-xs text-muted-foreground mt-2 tabular-nums">
                                                Remaining after this refund:{' '}
                                                {formatCurrency(remainingRefundable(remaining, selectedTx.amount), depositCurrency)}
                                            </p>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Remarks</Label>
                                        <Textarea
                                            value={remarks}
                                            onChange={(e) => setRemarks(e.target.value)}
                                            placeholder="Why is this deposit being refunded? (contract cancelled, overpayment, driver withdrew...)"
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Refund history */}
                            <div className="space-y-2">
                                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Refund History</Label>
                                {isLoadingRefunds ? (
                                    <div className="space-y-2">
                                        {[1, 2, 3].map((i) => (
                                            <Skeleton key={i} className="h-12 w-full" />
                                        ))}
                                    </div>
                                ) : refunds && refunds.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Description</TableHead>
                                                <TableHead>Remarks</TableHead>
                                                <TableHead className="text-right">Amount</TableHead>
                                                <TableHead className="w-12" />
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {refunds.map((r) => (
                                                <TableRow key={r.id}>
                                                    <TableCell className="text-muted-foreground whitespace-nowrap">
                                                        {formatBusinessDate(r.transaction_at)}
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-xs max-w-[200px]">
                                                        <span className="whitespace-normal break-all">{r.description || '-'}</span>
                                                    </TableCell>
                                                    <TableCell className="text-muted-foreground text-xs max-w-[200px]">
                                                        <span className="whitespace-normal break-all">{r.remarks || '-'}</span>
                                                    </TableCell>
                                                    <TableCell className="text-right font-medium tabular-nums text-red-600 dark:text-red-400">
                                                        −{formatCurrency(r.amount, r.currency)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                                            onClick={() => setPendingDelete(r)}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <p className="text-sm text-muted-foreground py-4">No refunds recorded for this deposit.</p>
                                )}
                            </div>
                        </div>

                        <div className="pt-4 border-t flex justify-end gap-2">
                            <Button variant="outline" onClick={handleClose}>Close</Button>
                            <Button
                                onClick={() => refundMutation.mutate()}
                                disabled={errors.length > 0 || refundMutation.isPending}
                            >
                                {refundMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                Record Refund
                            </Button>
                        </div>
                    </SheetContent>
                )}
            </Sheet>

            {/* Remove-refund confirmation */}
            <AlertDialog open={!!pendingDelete} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remove this refund?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This deletes the refund record only. The DEBIT transaction is not deleted — it returns to the
                            unassigned pool and can be reassigned.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => pendingDelete && deleteRefundMutation.mutate(pendingDelete.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteRefundMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Remove
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
